import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { syncService, SyncResult } from '../services/syncService';
import { coolingSystemConfigurationsKeys } from './queries/useCoolingSystemConfigurations';
import { largeUpsConfigurationsKeys } from './queries/useLargeUpsConfigurations';
import { liquidCoolingCduConfigurationsKeys } from './queries/useLiquidCoolingCduConfigurations';
import { powerDistributionConfigurationsKeys } from './queries/usePowerDistributionConfigurations';
import { rackConfigurationsKeys } from './queries/useRackConfigurations';
import { smallUpsConfigurationsKeys } from './queries/useSmallUpsConfigurations';

interface SectionReconciler {
  /** How many of this section's offline ops were flushed to the server. */
  getSyncedCount: (result: SyncResult) => number | undefined;
  /** React Query root key to invalidate when the count is > 0. */
  queryKey: readonly unknown[];
}

/**
 * Static table pairing each section's SyncResult "synced" count with its React
 * Query root key. When a section's offline flush synced > 0 ops, invalidating
 * the root key refetches the list so the optimistic offline copies (temp ids)
 * reconcile with the synced server copies (real ids) — fixing the transient
 * duplicate-until-manual-refresh seen after reconnect. Exported for testing.
 */
export const REPEATABLE_SECTION_SYNC_RECONCILERS: ReadonlyArray<SectionReconciler> =
  [
    {
      getSyncedCount: result => result.rackOperationsSynced,
      queryKey: rackConfigurationsKeys.all,
    },
    {
      getSyncedCount: result => result.smallUpsOperationsSynced,
      queryKey: smallUpsConfigurationsKeys.all,
    },
    {
      getSyncedCount: result => result.coolingSystemOperationsSynced,
      queryKey: coolingSystemConfigurationsKeys.all,
    },
    {
      getSyncedCount: result => result.liquidCoolingCduOperationsSynced,
      queryKey: liquidCoolingCduConfigurationsKeys.all,
    },
    {
      getSyncedCount: result => result.largeUpsOperationsSynced,
      queryKey: largeUpsConfigurationsKeys.all,
    },
    {
      getSyncedCount: result => result.powerDistributionOperationsSynced,
      queryKey: powerDistributionConfigurationsKeys.all,
    },
  ];

/**
 * Subscribe to offline-sync completion and refetch the repeatable-section lists
 * whose units were actually synced, so they reconcile without a manual page
 * refresh.
 *
 * The offline flush (`syncService.syncOffline*Operations`) POSTs each queued op
 * and remaps temp -> server ids in the offline store, but does not invalidate
 * the React Query cache, so the optimistic copy and the synced server copy both
 * render until the next refetch. `syncService` is a plain singleton with no
 * React context, so the invalidation must happen here, at a React call site
 * that owns a `QueryClient`. Mount this hook in the request-editor screens.
 *
 * Only sections with a synced count > 0 are invalidated (no needless refetch of
 * untouched sections). Each invalidation is isolated so one failure cannot block
 * the others.
 */
export function useReconcileRepeatableSectionsAfterSync(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const removeListener = syncService.addSyncListener((result: SyncResult) => {
      for (const {
        getSyncedCount,
        queryKey,
      } of REPEATABLE_SECTION_SYNC_RECONCILERS) {
        // Whole body isolated per section: a malformed SyncResult (e.g. a
        // throwing/NaN count) for one section must not abort the others.
        try {
          const syncedCount = getSyncedCount(result) ?? 0;
          if (!Number.isFinite(syncedCount) || syncedCount <= 0) {
            continue;
          }
          const invalidation = queryClient.invalidateQueries({ queryKey });
          if (invalidation && typeof invalidation.catch === 'function') {
            invalidation.catch((error: unknown) => {
              console.warn(
                `[offline-sync] Post-sync refetch rejected for ${String(
                  queryKey,
                )}:`,
                error,
              );
            });
          }
        } catch (error) {
          console.warn(
            `[offline-sync] Post-sync refetch failed for ${String(queryKey)}:`,
            error,
          );
        }
      }
    });

    return removeListener;
  }, [queryClient]);
}
